import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { DropdownOptions } from '../dropdown/DropdownOptions'
import { Input, InputField } from '../input'
import { NameTag } from '../name-tag'
import { Popover } from '../popover'
import { Pressable } from '../pressable'
import { ChevronDownGlyph } from '../primitive-glyphs/glyphs'

const SEARCH_DEBOUNCE_MS = 300

export interface MultiSelectAutocompleteProps<T> {
    /** Already-selected items, rendered as removable tags inside the field. */
    value: T[]
    /** Local options, filtered by `labelRenderer` as the user types. Ignored when `onSearch` is set. */
    items?: T[]
    /** Async lookup. Called with the trimmed query, debounced. */
    onSearch?(query: string): Promise<T[]>
    labelRenderer(item: T): string
    /** Tag content for a selected item. Defaults to `labelRenderer`. */
    valueRenderer?(item: T): ReactNode
    /** Identity for de-duplication and removal. Defaults to `labelRenderer`. */
    keyExtractor?(item: T): string
    onChange(value: T[]): void
    placeholder?: string
    emptyText?: string
    loadingText?: string
    errorText?: string
    removeAccessibilityLabel?: string
    disabled?: boolean
    isInvalid?: boolean
    className?: string
    nativeID?: string
    testID?: string
}

/**
 * Multi-value autocomplete: type to search a remote source, pick several, each
 * selection kept as a removable tag inside the field.
 *
 * Universal replacement for the legacy `aos-ui` `DropdownMultiAutocomplete`.
 * Composed from the same pieces as `Dropdown` — the Gluestack-backed `Popover`
 * for anchoring and `DropdownOptions` for the list, so the panel inherits the
 * shared option styling, the loading/empty/error states and the scroll
 * re-anchoring — with the field itself built on `Input` so it matches every
 * other form control.
 */
export function MultiSelectAutocomplete<T>({
    value,
    items = [],
    onSearch,
    labelRenderer,
    valueRenderer,
    keyExtractor,
    onChange,
    placeholder,
    emptyText,
    loadingText,
    errorText,
    removeAccessibilityLabel,
    disabled = false,
    isInvalid,
    className,
    nativeID,
    testID,
}: MultiSelectAutocompleteProps<T>) {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<T[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)

    const identity = useCallback(
        (item: T) => (keyExtractor ? keyExtractor(item) : labelRenderer(item)),
        [keyExtractor, labelRenderer],
    )

    // Latest-callback ref: an inline `onSearch` must not re-trigger the
    // debounce — searches key on the query alone.
    const onSearchRef = useRef(onSearch)
    React.useEffect(() => {
        onSearchRef.current = onSearch
    }, [onSearch])
    const hasSearch = !!onSearch

    React.useEffect(() => {
        if (!hasSearch) {
            return undefined
        }
        const trimmed = query.trim()
        if (!trimmed) {
            setResults([])
            setIsLoading(false)
            setIsError(false)
            return undefined
        }
        setIsLoading(true)
        setIsError(false)
        let cancelled = false
        const timer = setTimeout(() => {
            onSearchRef
                .current?.(trimmed)
                .then(found => {
                    if (!cancelled) {
                        setResults(found)
                        setIsLoading(false)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setResults([])
                        setIsError(true)
                        setIsLoading(false)
                    }
                })
        }, SEARCH_DEBOUNCE_MS)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [query, hasSearch])

    const options = useMemo(() => {
        if (hasSearch) {
            return results
        }
        const q = query.trim().toLowerCase()
        return q ? items.filter(item => labelRenderer(item).toLowerCase().includes(q)) : items
    }, [hasSearch, results, query, items, labelRenderer])

    // Never offer something already chosen.
    const selectable = useMemo(() => {
        const taken = new Set(value.map(identity))
        return options.filter(item => !taken.has(identity(item)))
    }, [options, value, identity])

    const select = (item: T) => {
        onChange([...value, item])
        setQuery('')
        setIsOpen(false)
    }

    const remove = (item: T) => {
        const key = identity(item)
        onChange(value.filter(v => identity(v) !== key))
    }

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            focusScope={false}
            placement='bottom left'
            offset={8}
            trigger={triggerProps => {
                const { ref: popoverRef, ...rest } = triggerProps as { ref?: React.Ref<unknown> }
                return (
                    <Input
                        {...rest}
                        ref={popoverRef as React.Ref<React.ComponentRef<typeof Input>>}
                        isDisabled={disabled}
                        isInvalid={isInvalid}
                        className={twMerge('h-auto min-h-10 flex-wrap py-1', className)}
                    >
                        {value.map(item => (
                            <NameTag
                                key={identity(item)}
                                onRemove={disabled ? undefined : () => remove(item)}
                                removeAccessibilityLabel={removeAccessibilityLabel}
                            >
                                {valueRenderer ? valueRenderer(item) : labelRenderer(item)}
                            </NameTag>
                        ))}
                        <Box className='min-w-[80px] flex-1'>
                            <InputField
                                value={query}
                                nativeID={nativeID}
                                testID={testID}
                                placeholder={value.length === 0 ? placeholder : undefined}
                                editable={!disabled}
                                onFocus={() => setIsOpen(true)}
                                onChangeText={next => {
                                    setQuery(next)
                                    setIsOpen(true)
                                }}
                            />
                        </Box>
                        <Pressable
                            accessibilityRole='button'
                            disabled={disabled}
                            onPress={() => setIsOpen(open => !open)}
                            className='h-6 w-6 shrink-0 items-center justify-center'
                        >
                            <ChevronDownGlyph size={16} />
                        </Pressable>
                    </Input>
                )
            }}
        >
            <DropdownOptions
                items={selectable}
                labelRenderer={labelRenderer}
                onSelect={select}
                emptyText={emptyText}
                loading={isLoading}
                loadingText={loadingText}
                error={isError}
                errorText={errorText}
            />
        </Popover>
    )
}

MultiSelectAutocomplete.displayName = 'MultiSelectAutocomplete'
